/**
 * Field problem badges (W2-06; DESIGN.md §4.11 "problem badges next to the offending field (click -> Problems)",
 * §5.10). Checker problems carry an inspector field path in `at.field` ('note.speed', 'line.attachUI',
 * 'segment.ease', ... as in core/check/types.ts); the inspector shows a badge next to that field when the problem
 * is about the inspected entities. The badge carries the problem's own message as its tooltip and opens the
 * Problems panel on click.
 *
 * CR-W3-03-10 (UX-029 / UX-030): the 30k chart holds ~66 000 problems, and every inspector field used to filter
 * that whole list per render. The list is now indexed once per list identity (`buildProblemIndex`), `badges(field)`
 * reads the field's bucket and is memoised per (list, field, scope), and the hook re-renders the section only when
 * a problem overlapping the inspected entities actually changes — not on every problem-list notification.
 */

import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { Problem, Severity } from '../../core/check/types';
import type { KeyId, LineId, NoteId } from '../../core/model/types';
import type { BadgeProps } from '../../ui/types';
import type { Unsubscribe } from '../../core/util/types';

/** Inspector field paths (`ProblemLocation.field`); also the `helpId` of the field ("Show me", focusField). */
export const FIELD = Object.freeze({
  noteKind: 'note.kind',
  noteTime: 'note.t',
  noteEnd: 'note.end',
  noteX: 'note.x',
  noteSide: 'note.above',
  noteFake: 'note.fake',
  noteSpeed: 'note.speed',
  noteYOffset: 'note.yOffset',
  noteSize: 'note.size',
  noteAlpha: 'note.alpha',
  noteVisible: 'note.visibleTime',
  noteHitsound: 'note.hitsound',
  noteTint: 'note.tint',
  noteTintHit: 'note.tintHitEffects',
  noteJudge: 'note.judgeArea',
  keyBeat: 'key.t',
  keyValue: 'key.value',
  keyJump: 'key.jump',
  segmentEase: 'segment.ease',
  segmentStay: 'segment.stay',
  segmentFrom: 'segment.from',
  segmentTo: 'segment.to',
  segmentSlice: 'segment.slice',
  segmentTextMode: 'segment.textMode',
  lineName: 'line.name',
  lineType: 'line.type',
  lineParent: 'line.parent',
  lineRotateWithParent: 'line.rotateWithParent',
  lineZOrder: 'line.zOrder',
  lineCover: 'line.cover',
  lineGroup: 'line.group',
  lineTag: 'line.tag',
  lineScaleX: 'line.scaleX',
  lineScaleY: 'line.scaleY',
  lineTexture: 'line.texture',
  lineAttachUI: 'line.attachUI',
  lineGifAutoLoop: 'line.gifAutoLoop',
  lineOpacity: 'line.alpha',
  lineColor: 'line.color',
  lineIncline: 'line.incline',
  lineText: 'line.text',
  lineSpeed: 'line.speed',
  linePaint: 'line.paint',
  lineLayers: 'line.layers',
  lineBpmfactor: 'line.rpe.bpmfactor',
  lineAnchor: 'line.rpe.anchor',
  lineSkewControl: 'line.rpe.skewControl',
  bpmBeat: 'bpm.t',
  bpmValue: 'bpm.bpm',
  markerName: 'marker.name',
  markerBeat: 'marker.t',
  metaMusic: 'meta.music',
  metaPreview: 'meta.previewStart',
  pkgRpeVersion: 'pkg.rpeVersion',
  pkgSpeedMode: 'pkg.speedMode',
  pkgAspect: 'pkg.aspectRatio',
  pkgAttachUiFix: 'pkg.useAttachUiFix',
});

/** Field path of a note control curve (same form as the checker). */
export function controlField(which: 'pos' | 'alpha' | 'size' | 'y'): string {
  return `line.controls.${which}`;
}

/** Field path of a line property (`line.alpha`, `line.scaleX`...). */
export function linePropField(prop: string): string {
  return `line.${prop}`;
}

/** The inspected entities a badge must be about. */
export interface ProblemScope {
  readonly notes?: readonly NoteId[];
  readonly keys?: readonly KeyId[];
  readonly lines?: readonly LineId[];
}

const SEVERITY_RANK: Readonly<Record<Severity, number>> = { fatal: 0, error: 1, warning: 2, tip: 3 };

function overlaps<T>(a: readonly T[] | undefined, b: ReadonlySet<T>): boolean {
  if (a === undefined) return false;
  for (const x of a) if (b.has(x)) return true;
  return false;
}

/**
 * A problem list indexed for the inspector (CR-W3-03-10): by `at.field` for `problemsFor`, and by the entity ids
 * a scope can name, so a scope signature is O(scope) instead of O(problems).
 */
export interface ProblemIndex {
  readonly byField: ReadonlyMap<string, readonly Problem[]>;
  readonly byNote: ReadonlyMap<NoteId, readonly Problem[]>;
  readonly byKey: ReadonlyMap<KeyId, readonly Problem[]>;
  readonly byLine: ReadonlyMap<LineId, readonly Problem[]>;
  /** Problems whose `at` names no note / key / line (chart fields, BPM, markers...). */
  readonly byEntity: readonly Problem[];
}

/** Builds the index once per problem-list identity. */
export function buildProblemIndex(problems: readonly Problem[]): ProblemIndex {
  const byField = new Map<string, Problem[]>();
  const byNote = new Map<NoteId, Problem[]>();
  const byKey = new Map<KeyId, Problem[]>();
  const byLine = new Map<LineId, Problem[]>();
  const byEntity: Problem[] = [];
  const push = <K>(map: Map<K, Problem[]>, k: K, p: Problem): void => {
    const bucket = map.get(k);
    if (bucket === undefined) map.set(k, [p]);
    else bucket.push(p);
  };
  for (const p of problems) {
    push(byField, p.at.field, p);
    const notes = p.at.notes ?? [];
    const keys = p.at.keys ?? [];
    const lines = p.at.lines ?? [];
    const line = p.at.line;
    if (notes.length === 0 && keys.length === 0 && line === undefined && lines.length === 0) {
      byEntity.push(p);
      continue;
    }
    for (const n of notes) push(byNote, n, p);
    for (const k of keys) push(byKey, k, p);
    if (line !== undefined) push(byLine, line, p);
    for (const l of lines) push(byLine, l, p);
  }
  return { byField, byNote, byKey, byLine, byEntity };
}

function listAt(source: readonly Problem[] | ProblemIndex, field: string): readonly Problem[] {
  if ('byField' in source) return source.byField.get(field) ?? [];
  return source.filter((p) => p.at.field === field);
}

/**
 * Problems about `field` of the scope's entities: note fields match by note ids, key and segment fields by key
 * ids, line fields by line, chart fields (meta., pkg.) always. Most severe first. Accepts a plain list or the
 * index `buildProblemIndex` produces (the hook uses the index: the list is filtered once, not per field).
 */
export function problemsFor(
  source: readonly Problem[] | ProblemIndex,
  field: string,
  scope: ProblemScope,
): Problem[] {
  const notes = new Set(scope.notes ?? []);
  const keys = new Set(scope.keys ?? []);
  const lines = new Set(scope.lines ?? []);
  const chartLevel = field.startsWith('meta.') || field.startsWith('pkg.');
  const out = listAt(source, field).filter((p) => {
    if (chartLevel) return true;
    if (field.startsWith('note.')) return overlaps(p.at.notes, notes);
    if (field.startsWith('key.') || field.startsWith('segment.')) return overlaps(p.at.keys, keys);
    if (field.startsWith('line.')) {
      if (p.at.line !== undefined && lines.has(p.at.line)) return true;
      return overlaps(p.at.lines, lines);
    }
    return (
      overlaps(p.at.notes, notes) || overlaps(p.at.keys, keys) || (p.at.line !== undefined && lines.has(p.at.line))
    );
  });
  return out.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
}

/**
 * Identity of the problems a scope can show a badge for (CR-W3-03-10 item 3): a problem-list change whose
 * overlapping problems are the same does not re-render the section.
 */
function scopeSignature(index: ProblemIndex, scope: ProblemScope): string {
  const seen = new Set<Problem>(index.byEntity);
  const add = (bucket: readonly Problem[] | undefined): void => {
    if (bucket === undefined) return;
    for (const p of bucket) seen.add(p);
  };
  for (const n of scope.notes ?? []) add(index.byNote.get(n));
  for (const k of scope.keys ?? []) add(index.byKey.get(k));
  for (const l of scope.lines ?? []) add(index.byLine.get(l));
  const parts: string[] = [];
  for (const p of seen) parts.push(`${p.id} ${p.severity} ${JSON.stringify(p.message)}`);
  parts.sort();
  return parts.join('\n');
}

/** Opens the Problems dock tab (or the dock when no Problems panel is registered). */
export function openProblems(services: AppServices): void {
  const panel = services.registry.panels('chart.dock').find((p) => p.id.toLowerCase().includes('problem'));
  if (panel !== undefined) services.ui.showPanel(panel.id);
  else services.ui.focusRegion('chart.dock');
}

/** One badge per severity present (most severe first), each with the first problem's message as tooltip. */
export function problemBadges(list: readonly Problem[], onOpen: () => void): BadgeProps[] {
  const out: BadgeProps[] = [];
  const seen = new Set<Severity>();
  for (const p of list) {
    if (seen.has(p.severity)) continue;
    seen.add(p.severity);
    out.push({ kind: 'problem', severity: p.severity, tooltip: p.message, onClick: onOpen });
  }
  return out;
}

interface BadgeCacheEntry {
  readonly list: readonly Problem[];
  readonly scope: ProblemScope;
  readonly value: readonly BadgeProps[];
}

/**
 * `badges(field)` for the inspected entities. Re-renders only when a problem overlapping the scope changes
 * (CR-W3-03-10), and each field's badge array is stable across renders that do not change it.
 */
export function useFieldProblems(
  services: AppServices,
  scope: ProblemScope,
): (field: string) => readonly BadgeProps[] {
  const problems = services.problems;
  const notesKey = (scope.notes ?? []).join(',');
  const keysKey = (scope.keys ?? []).join(',');
  const linesKey = (scope.lines ?? []).join(',');
  // The scope arrays are usually rebuilt on every render; their contents decide.
  const stableScope = useMemo<ProblemScope>(
    () => ({ notes: idList<NoteId>(notesKey), keys: idList<KeyId>(keysKey), lines: idList<LineId>(linesKey) }),
    [notesKey, keysKey, linesKey],
  );

  // One snapshot state per hook call: the indexed list, its identity, the scope signature and the per-field
  // badge cache. `getSnapshot` is idempotent, so React may call it as often as it likes.
  const state = useRef<{
    list: readonly Problem[] | null;
    scope: ProblemScope | null;
    index: ProblemIndex | null;
    sig: string;
    badges: Map<string, BadgeCacheEntry>;
  } | null>(null);
  if (state.current === null) state.current = { list: null, scope: null, index: null, sig: '', badges: new Map() };

  const subscribe = useCallback((cb: () => void): Unsubscribe => problems.subscribe(cb), [problems]);
  const getSnapshot = useCallback((): string => {
    const st = state.current;
    if (st === null) return '';
    const list = problems.problems();
    if (st.list !== list || st.scope !== stableScope) {
      st.list = list;
      st.scope = stableScope;
      st.index = buildProblemIndex(list);
      st.badges.clear();
      st.sig = scopeSignature(st.index, stableScope);
    }
    return st.sig;
  }, [problems, stableScope]);
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return useCallback(
    (field: string): readonly BadgeProps[] => {
      const st = state.current;
      const list = st?.list ?? problems.problems();
      const index = st?.index ?? buildProblemIndex(list);
      const cached = st?.badges.get(field);
      if (cached !== undefined && cached.list === list && cached.scope === stableScope) return cached.value;
      const value = problemBadges(problemsFor(index, field, stableScope), () => {
        openProblems(services);
      });
      st?.badges.set(field, { list, scope: stableScope, value });
      return value;
    },
    [problems, stableScope, services],
  );
}

function idList<T extends number>(key: string): T[] {
  return key === '' ? [] : key.split(',').map((x) => Number(x) as T);
}
